<!-- =========================================================================================
    Description: Visual canvas for designing a screen anchor layout at reference resolution.
    Objects can be dragged to reposition and resized via corner handles.
    Drop hobjects from the asset library to add them to the canvas.
========================================================================================== -->

<template>
  <div class="canvas-container" ref="container">

    <!-- Zoom controls -->
    <div class="zoom-controls">
      <vs-button size="small" type="flat" icon-pack="feather" icon="icon-zoom-in"  @click="zoomIn" />
      <span class="zoom-label">{{ Math.round(zoom * 100) }}%</span>
      <vs-button size="small" type="flat" icon-pack="feather" icon="icon-zoom-out" @click="zoomOut" />
      <vs-button size="small" type="flat" @click="zoomReset">Fit</vs-button>
    </div>

    <!-- Scrollable viewport -->
    <div
      class="canvas-viewport"
      @dragover.prevent
      @drop="onDrop($event)"
    >
      <!-- The canvas itself, scaled to reference resolution -->
      <div
        class="canvas-stage"
        :style="stageStyle"
        ref="stage"
        @click.self="selectedNodePid = null"
      >

        <!-- Safe area shading (when enabled) -->
        <div v-if="config.safeArea" class="safe-area-overlay"></div>

        <!-- Anchor-point crosshair -->
        <div class="anchor-point" :style="anchorPointStyle">
          <div class="anchor-point__h"></div>
          <div class="anchor-point__v"></div>
        </div>

        <!-- Placed nodes -->
        <div
          v-for="node in nodes"
          :key="node.pid"
          class="canvas-node"
          :class="{ 'canvas-node--selected': selectedNodePid === node.pid }"
          :style="nodeStyle(node)"
          :data-node-pid="node.pid"
          @click.stop="selectNode(node)"
        >
          <!-- Hobject thumbnail / label -->
          <img
            v-if="getHobject(node.hobject_pid) && getHobject(node.hobject_pid).image"
            :src="getHobject(node.hobject_pid).image"
            class="canvas-node__img"
            draggable="false"
          />
          <div v-else class="canvas-node__label">
            {{ getHobject(node.hobject_pid) ? getHobject(node.hobject_pid).name : node.hobject_pid }}
          </div>

          <!-- Resize handles (visible only when selected) -->
          <template v-if="selectedNodePid === node.pid">
            <div class="resize-handle resize-handle--nw" @mousedown.stop="startResize(node, 'nw', $event)"></div>
            <div class="resize-handle resize-handle--ne" @mousedown.stop="startResize(node, 'ne', $event)"></div>
            <div class="resize-handle resize-handle--sw" @mousedown.stop="startResize(node, 'sw', $event)"></div>
            <div class="resize-handle resize-handle--se" @mousedown.stop="startResize(node, 'se', $event)"></div>
          </template>
        </div>

      </div>
    </div>
  </div>
</template>

<script>
import interact from 'interactjs'

const DEFAULT_HOBJECT_SIZE = 200

export default {
  name: 'ScreenAnchorCanvas',

  props: {
    anchor: { type: Object, required: true },
    nodes: { type: Array, default: () => [] },
    hobjects: { type: Array, default: () => [] },
    selectedNodePid: { type: String, default: null },
  },

  data() {
    return {
      zoom: 1,
      interactInstances: [],
    }
  },

  computed: {
    config() {
      try {
        return JSON.parse(this.anchor.mark)
      } catch {
        return { referenceWidth: 1920, referenceHeight: 1080, scaleMode: 'letterbox', safeArea: true }
      }
    },

    stageStyle() {
      return {
        width:  `${parseFloat(this.config.referenceWidth) || 1080}px`,
        height: `${parseFloat(this.config.referenceHeight) || 1920}px`,
        transform: `scale(${this.zoom})`,
        transformOrigin: 'center center',
      }
    },

    // CSS position of the anchor-point crosshair within the canvas
    anchorPointStyle() {
      const positions = {
        'top-left':      { top:  '0',    left:  '0' },
        'top-center':    { top:  '0',    left:  '50%' },
        'top-right':     { top:  '0',    right: '0' },
        'middle-left':   { top:  '50%',  left:  '0' },
        'middle-center': { top:  '50%',  left:  '50%' },
        'middle-right':  { top:  '50%',  right: '0' },
        'bottom-left':   { bottom: '0',  left:  '0' },
        'bottom-center': { bottom: '0',  left:  '50%' },
        'bottom-right':  { bottom: '0',  right: '0' },
      }
      return positions[this.config.anchorPosition] || positions['bottom-right']
    },
  },

  watch: {
    nodes: {
      handler() {
        this.$nextTick(() => this.bindInteract())
      },
      deep: false,
    },
    config: {
      handler() {
        // Refit canvas when config changes (e.g., resolution change)
        this.$nextTick(() => this.fitToContainer())
      },
      deep: true,
    },
  },

  mounted() {
    console.log('[ScreenAnchorCanvas] Mounted with nodes:', this.nodes.length, this.nodes)
    this.$nextTick(() => {
      this.fitToContainer()
      this.bindInteract()
    })
  },

  beforeDestroy() {
    this.unbindInteract()
  },

  methods: {
    // ── Zoom ────────────────────────────────────────────────────────────────────

    zoomIn()    { this.zoom = Math.min(this.zoom * 1.25, 3) },
    zoomOut()   { this.zoom = Math.max(this.zoom / 1.25, 0.05) },
    zoomReset() { this.fitToContainer() },

    fitToContainer() {
      const container = this.$refs.container
      if (!container) return

      // Get actual available space
      // Leave generous padding for sidebars and margins
      const horizontalPadding = 600 // Account for left sidebar (280px) + right sidebar (280px) + margins
      const verticalPadding = 150 // Top and bottom padding

      const availableWidth = Math.max(container.clientWidth - horizontalPadding, 300)
      const availableHeight = Math.max(container.clientHeight - verticalPadding, 300)

      const refW = parseFloat(this.config.referenceWidth) || 1080
      const refH = parseFloat(this.config.referenceHeight) || 1920

      const scaleW = availableWidth / refW
      const scaleH = availableHeight / refH

      // Use the smaller scale to ensure the entire canvas fits
      // Use 0.85 factor to ensure it's comfortably visible
      this.zoom = Math.min(scaleW, scaleH) * 0.85

      console.log('[ScreenAnchorCanvas] Fit to container:', {
        containerSize: { w: container.clientWidth, h: container.clientHeight },
        availableSize: { w: availableWidth, h: availableHeight },
        referenceSize: { w: refW, h: refH },
        zoom: this.zoom
      })
    },

    // ── Node style ───────────────────────────────────────────────────────────────

    nodeStyle(node) {
      // Get the anchor position from the node's anchor_position property (if stored per-node)
      // or fall back to the anchor's global anchor position
      const anchorPos = node.anchor_position || this.config.anchorPosition || 'bottom-right'

      // Compute absolute position based on anchor point
      const { left, top } = this.computeAbsolutePosition(
        node.x || 0,
        node.y || 0,
        node.width || DEFAULT_HOBJECT_SIZE,
        node.height || DEFAULT_HOBJECT_SIZE,
        anchorPos
      )

      return {
        position: 'absolute',
        left:   `${left}px`,
        top:    `${top}px`,
        width:  `${node.width  || DEFAULT_HOBJECT_SIZE}px`,
        height: `${node.height || DEFAULT_HOBJECT_SIZE}px`,
        transform: `scale(${node.scale || 1})`,
        transformOrigin: 'top left',
        cursor: 'grab',
      }
    },

    // Convert relative coordinates (from anchor point to object CENTER) to absolute canvas coordinates (top-left)
    // relX, relY: offset from screen anchor point to object center
    // X axis: always left-to-right (positive = right, negative = left)
    // Y axis: origin at BOTTOM, positive = up, negative = down
    computeAbsolutePosition(relX, relY, width, height, anchorPosition) {
      // Parse as numbers to ensure proper math operations (config values might be strings)
      const refW = parseFloat(this.config.referenceWidth) || 1080
      const refH = parseFloat(this.config.referenceHeight) || 1920

      // Safety check
      if (!refW || !refH || isNaN(refW) || isNaN(refH)) {
        console.error('[computeAbsolutePosition] Invalid config dimensions!', this.config)
        return { left: 0, top: 0 }
      }

      // First, get the screen anchor point coordinates (in bottom-origin system)
      // Y coordinate is measured from BOTTOM of screen
      let anchorX = 0
      let anchorY = 0

      switch (anchorPosition) {
        case 'top-left':
          anchorX = 0
          anchorY = refH // Top is at max Y (bottom-origin)
          break
        case 'top-center':
          anchorX = refW / 2
          anchorY = refH
          break
        case 'top-right':
          anchorX = refW
          anchorY = refH
          break
        case 'middle-left':
          anchorX = 0
          anchorY = refH / 2
          break
        case 'middle-center':
          anchorX = refW / 2
          anchorY = refH / 2
          break
        case 'middle-right':
          anchorX = refW
          anchorY = refH / 2
          break
        case 'bottom-left':
          anchorX = 0
          anchorY = 0 // Bottom is at Y=0
          break
        case 'bottom-center':
          anchorX = refW / 2
          anchorY = 0
          break
        case 'bottom-right':
          anchorX = refW
          anchorY = 0
          break
      }

      // Calculate object center position in bottom-origin coordinates
      // relY is positive upward, negative downward
      const centerX = anchorX + relX
      const centerY = anchorY + relY

      // Convert from bottom-origin to CSS top-origin
      const left = centerX - width / 2
      const top = (refH - centerY) - height / 2 // Flip Y axis for CSS

      return { left, top }
    },

    // Convert screen space movement delta to relative coordinate delta
    // Since X is always left-to-right and Y is always top-to-bottom,
    // screen deltas map directly to relative coordinate deltas
    screenDeltaToRelativeDelta(screenDx, screenDy) {
      // No conversion needed - coordinates are consistent
      return { dx: screenDx, dy: screenDy }
    },

    // ── Selection ────────────────────────────────────────────────────────────────

    selectNode(node) {
      this.$emit('update:selectedNodePid', node.pid)
    },

    // ── Interact.js drag & resize ─────────────────────────────────────────────

    bindInteract() {
      this.unbindInteract()

      const stageEl = this.$refs.stage
      if (!stageEl) return

      const nodeEls = stageEl.querySelectorAll('.canvas-node')

      nodeEls.forEach(el => {
        let totalDx = 0
        let totalDy = 0

        const instance = interact(el)
          .draggable({
            listeners: {
              start: () => {
                const nodePid = el.dataset.nodePid
                const node = this.nodes.find(n => n.pid === nodePid)
                if (!node) return

                // Reset accumulated deltas
                totalDx = 0
                totalDy = 0

                this.$emit('node-drag-start', node)
              },
              move: (event) => {
                // Accumulate the deltas for the final position calculation
                // Adjust for canvas zoom so 1px drag = 1px in reference space
                totalDx += event.dx / this.zoom
                totalDy += event.dy / this.zoom

                // interact.js automatically handles visual feedback via DOM transforms
                // We don't update the store here - node data will be updated when command executes
              },
              end: () => {
                // Calculate final position and emit
                const nodePid = el.dataset.nodePid
                const node = this.nodes.find(n => n.pid === nodePid)
                if (!node) return

                // Apply accumulated deltas to get final position
                // Y screen delta needs inversion for bottom-origin system
                const finalNode = {
                  ...node,
                  x: (node.x || 0) + totalDx,
                  y: (node.y || 0) - totalDy  // Invert Y
                }

                this.$emit('node-moved', finalNode)
              },
            },
            modifiers: [],
          })
          .resizable({
            edges: { left: true, right: true, bottom: true, top: true },
            listeners: {
              start: () => {
                const nodePid = el.dataset.nodePid
                const node = this.nodes.find(n => n.pid === nodePid)
                if (!node) return
                this.$emit('node-resize-start', node)
              },
              move: () => {
                // interact.js automatically handles visual feedback via DOM rect changes
                // We don't update the store here - node data will be updated when command executes
              },
              end: (event) => {
                // Calculate final size and position based on total resize
                const nodePid = el.dataset.nodePid
                const node = this.nodes.find(n => n.pid === nodePid)
                if (!node) return

                // Get the total deltas from the resize event
                const widthDelta = event.rect.width / this.zoom - (node.width || DEFAULT_HOBJECT_SIZE)
                const heightDelta = event.rect.height / this.zoom - (node.height || DEFAULT_HOBJECT_SIZE)
                const leftDelta = event.deltaRect.left / this.zoom
                const topDelta = event.deltaRect.top / this.zoom

                // Adjust center position based on which edges were dragged
                let centerDx = 0
                let centerDy = 0

                if (leftDelta !== 0) centerDx = leftDelta + widthDelta / 2
                else if (widthDelta !== 0) centerDx = widthDelta / 2

                // Y inversion: topDelta in screen space (positive = down) affects bottom-origin Y (negative = down)
                if (topDelta !== 0) centerDy = -(topDelta + heightDelta / 2)
                else if (heightDelta !== 0) centerDy = -heightDelta / 2

                const finalNode = {
                  ...node,
                  width:  event.rect.width / this.zoom,
                  height: event.rect.height / this.zoom,
                  x: (node.x || 0) + centerDx,
                  y: (node.y || 0) + centerDy,
                }

                this.$emit('node-resized', finalNode)
              },
            },
            modifiers: [
              interact.modifiers.restrictSize({ min: { width: 20, height: 20 } }),
            ],
          })

        this.interactInstances.push(instance)
      })
    },

    unbindInteract() {
      this.interactInstances.forEach(i => {
        try {
          i.unset()
        } catch (err) {
          // Silently ignore errors during cleanup
          console.debug('[ScreenAnchorCanvas] Error unbinding interact instance:', err)
        }
      })
      this.interactInstances = []
    },

    // Manual resize handles (fallback / fine-grain control) ─────────────────────

    startResize(node, handle, event) {
      event.preventDefault()

      // Emit start event for undo/redo tracking
      this.$emit('node-resize-start', node)

      const startX = event.clientX
      const startY = event.clientY
      const startW = node.width  || DEFAULT_HOBJECT_SIZE
      const startH = node.height || DEFAULT_HOBJECT_SIZE
      const startNodeX = node.x  || 0
      const startNodeY = node.y  || 0

      // Track the element for visual feedback
      const el = event.target.closest('.canvas-node')
      if (!el) return

      const onMove = (e) => {
        const dx = (e.clientX - startX) / this.zoom
        const dy = (e.clientY - startY) / this.zoom

        // Calculate new size and center position based on which handle is being dragged
        let newWidth = startW
        let newHeight = startH
        let centerDx = 0
        let centerDy = 0

        if (handle.includes('e')) {
          // Dragging east edge - width increases, center moves right
          newWidth = Math.max(20, startW + dx)
          centerDx = (newWidth - startW) / 2
        }
        if (handle.includes('w')) {
          // Dragging west edge - width changes, center moves
          newWidth = Math.max(20, startW - dx)
          centerDx = dx / 2
        }
        if (handle.includes('s')) {
          // Dragging south edge (bottom in CSS) - with Y inversion for bottom-origin
          newHeight = Math.max(20, startH + dy)
          centerDy = -(newHeight - startH) / 2  // Invert Y
        }
        if (handle.includes('n')) {
          // Dragging north edge (top in CSS) - with Y inversion for bottom-origin
          newHeight = Math.max(20, startH - dy)
          centerDy = -dy / 2  // Invert Y
        }

        // Update element visually via CSS (don't mutate store)
        el.style.width = `${newWidth}px`
        el.style.height = `${newHeight}px`
        el.style.left = `${parseFloat(el.style.left) + centerDx}px`
        el.style.top = `${parseFloat(el.style.top) + centerDy}px`
      }

      const onUp = (e) => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)

        // Calculate final position
        const dx = (e.clientX - startX) / this.zoom
        const dy = (e.clientY - startY) / this.zoom

        let newWidth = startW
        let newHeight = startH
        let centerDx = 0
        let centerDy = 0

        if (handle.includes('e')) {
          newWidth = Math.max(20, startW + dx)
          centerDx = (newWidth - startW) / 2
        }
        if (handle.includes('w')) {
          newWidth = Math.max(20, startW - dx)
          centerDx = dx / 2
        }
        if (handle.includes('s')) {
          newHeight = Math.max(20, startH + dy)
          centerDy = -(newHeight - startH) / 2
        }
        if (handle.includes('n')) {
          newHeight = Math.max(20, startH - dy)
          centerDy = -dy / 2
        }

        const finalNode = {
          ...node,
          width: newWidth,
          height: newHeight,
          x: startNodeX + centerDx,
          y: startNodeY + centerDy,
        }

        this.$emit('node-resized', finalNode)
      }

      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },

    // ── Drop from asset library ───────────────────────────────────────────────

    onDrop(event) {
      const hobjectPid = event.dataTransfer.getData('hobject_pid')
      if (!hobjectPid) return

      const stageRect = this.$refs.stage.getBoundingClientRect()
      // Convert screen drop position to absolute canvas coordinates
      const absX = (event.clientX - stageRect.left) / this.zoom
      const absY = (event.clientY - stageRect.top)  / this.zoom

      const hobject = this.hobjects.find(h => h.pid === hobjectPid)
      const width  = (hobject && hobject.width)  ? hobject.width  : DEFAULT_HOBJECT_SIZE
      const height = (hobject && hobject.height) ? hobject.height : DEFAULT_HOBJECT_SIZE

      // Determine anchor position based on drop location (closest corner/edge)
      const anchorPos = this.determineAnchorPositionFromDrop(absX, absY)

      // Convert absolute canvas coordinates to relative coordinates based on anchor position
      const { x, y } = this.absoluteToRelativePosition(absX, absY, anchorPos)

      this.$emit('hobject-dropped', { hobject_pid: hobjectPid, x, y, width, height, anchor_position: anchorPos })
    },

    // Determine the best anchor position based on where the object was dropped
    determineAnchorPositionFromDrop(absX, absY) {
      const refW = parseFloat(this.config.referenceWidth) || 1080
      const refH = parseFloat(this.config.referenceHeight) || 1920

      // Calculate which third of the screen (horizontally and vertically)
      const horizontalThird = absX < refW / 3 ? 'left' : absX > 2 * refW / 3 ? 'right' : 'center'
      const verticalThird = absY < refH / 3 ? 'top' : absY > 2 * refH / 3 ? 'bottom' : 'middle'

      // Construct anchor position from the thirds
      return `${verticalThird}-${horizontalThird}`
    },

    // Convert absolute canvas coordinates (drop point) to relative coordinates
    // absX, absY: the drop point on canvas (in CSS/screen coordinates)
    // Returns: offset from screen anchor point to object center (in bottom-origin system)
    absoluteToRelativePosition(absX, absY, anchorPosition) {
      const refW = parseFloat(this.config.referenceWidth) || 1080
      const refH = parseFloat(this.config.referenceHeight) || 1920

      // First, get the screen anchor point coordinates in bottom-origin system
      let anchorX = 0
      let anchorY = 0

      switch (anchorPosition) {
        case 'top-left':
          anchorX = 0
          anchorY = refH  // Top is at max Y in bottom-origin
          break
        case 'top-center':
          anchorX = refW / 2
          anchorY = refH
          break
        case 'top-right':
          anchorX = refW
          anchorY = refH
          break
        case 'middle-left':
          anchorX = 0
          anchorY = refH / 2
          break
        case 'middle-center':
          anchorX = refW / 2
          anchorY = refH / 2
          break
        case 'middle-right':
          anchorX = refW
          anchorY = refH / 2
          break
        case 'bottom-left':
          anchorX = 0
          anchorY = 0  // Bottom is at Y=0 in bottom-origin
          break
        case 'bottom-center':
          anchorX = refW / 2
          anchorY = 0
          break
        case 'bottom-right':
          anchorX = refW
          anchorY = 0
          break
      }

      // Convert CSS/screen drop point to bottom-origin coordinates
      const centerX = absX
      const centerY = refH - absY  // Flip Y axis

      // Calculate offset from anchor point to object center
      // X is always left-to-right, Y is bottom-origin (positive = up)
      const x = centerX - anchorX
      const y = centerY - anchorY

      return { x, y }
    },

    // ── Helpers ──────────────────────────────────────────────────────────────────

    getHobject(pid) {
      return this.hobjects.find(h => h.pid === pid) || null
    },
  },
}
</script>

<style lang="scss" scoped>
.canvas-container {
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  min-height: 400px;
  background: #1a1a2e;
  border-radius: 6px;
  overflow: hidden;
}

.zoom-controls {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  background: rgba(0,0,0,0.5);
  z-index: 10;

  .zoom-label {
    color: #fff;
    font-size: 0.75rem;
    min-width: 40px;
    text-align: center;
  }
}

.canvas-viewport {
  flex: 1;
  background-color: #fff;
  overflow: auto;
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 0; // Allow flex child to shrink
}

.canvas-stage {
  position: relative;
  background: #f0f0f0;
  box-shadow: 0 4px 32px rgba(0,0,0,0.6);
  flex-shrink: 0;
  border: 10px solid rgb(24, 24, 24); // More visible border to represent phone screen
  border-radius: 50px; // Slight rounding for phone-like appearance
}

// Safe area shading: semi-transparent inset to visualise notch margins (~4% typical)
.safe-area-overlay {
  position: absolute;
  inset: 0;
  border: 44px solid rgba(0,0,0,0.2);
  pointer-events: none;
  z-index: 0;
}

// Crosshair at the anchor point
.anchor-point {
  position: absolute;
  width: 24px;
  height: 24px;
  transform: translate(-50%, -50%);
  z-index: 2;
  pointer-events: none;

  &__h,
  &__v {
    position: absolute;
    background: rgba(115, 103, 240, 0.85);
  }

  &__h {
    width: 24px;
    height: 2px;
    top: 11px;
    left: 0;
  }

  &__v {
    width: 2px;
    height: 24px;
    left: 11px;
    top: 0;
  }
}

.canvas-node {
  box-sizing: border-box;
  border: 1px dashed rgba(255,255,255,0.25);
  overflow: hidden;
  user-select: none;

  &--selected {
    border: 2px solid #7367f0;
    box-shadow: 0 0 0 2px rgba(115, 103, 240, 0.4);
  }
}

.canvas-node__img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
  pointer-events: none;
}

.canvas-node__label {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: rgba(255,255,255,0.7);
  background: rgba(115, 103, 240, 0.2);
  text-align: center;
  padding: 4px;
  word-break: break-word;
}

// Corner resize handles
.resize-handle {
  position: absolute;
  width: 10px;
  height: 10px;
  background: #7367f0;
  border: 2px solid #fff;
  border-radius: 2px;
  z-index: 10;

  &--nw { top: -5px;  left: -5px;  cursor: nw-resize; }
  &--ne { top: -5px;  right: -5px; cursor: ne-resize; }
  &--sw { bottom: -5px; left: -5px;  cursor: sw-resize; }
  &--se { bottom: -5px; right: -5px; cursor: se-resize; }
}
</style>
